import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { StatusBadge } from "@/components/RoleBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { CalendarDays, Check, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { DatePicker, fmtDate } from "@/components/DatePicker";
import { LeaveCalendar } from "@/components/LeaveCalendar";
import { PtoCalculator } from "@/components/PtoCalculator";
import { PtoPolicyCard } from "@/components/PtoPolicy";
import { PtoCategoriesCard } from "@/components/PtoCategories";

const TYPES = ["Vacation", "Sick Leave", "Personal", "Bereavement", "Unpaid"];

export default function Leave() {
  const { user } = useAuth();
  const { activeCompanyId } = useCompany();
  const [items, setItems] = useState([]);
  const isEmployee = user.role === "employee";
  const [form, setForm] = useState({ type: "Vacation", start_date: "", end_date: "", days: 1, reason: "" });

  const load = () => {
    const params = user.role === "bureau_admin" ? { company_id: activeCompanyId } : {};
    if (user.role === "bureau_admin" && !activeCompanyId) return;
    api.get("/leave", { params }).then((r) => setItems(r.data)).catch(() => {});
    if (!isEmployee) api.get("/employees", { params }).then((r) => setEmployees(r.data.filter((e) => e.worker_type !== "contractor"))).catch(() => {});
  };
  const [employees, setEmployees] = useState([]);
  const [catKey, setCatKey] = useState(0);
  const [types, setTypes] = useState(TYPES);
  useEffect(() => { api.get("/pto/categories", { params: user.role === "bureau_admin" ? { company_id: activeCompanyId } : {} }).then((r) => { const t = r.data.flatMap((c) => c.leave_types || [c.name]); if (t.length) { setTypes(t); setForm((f) => ({ ...f, type: t.includes(f.type) ? f.type : t[0] })); } }).catch(() => {}); }, [activeCompanyId, catKey]); // eslint-disable-line
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [activeCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    try {
      await api.post("/leave", form);
      toast.success("Leave request submitted");
      setForm({ type: "Vacation", start_date: "", end_date: "", days: 1, reason: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setStatus = async (id, status) => {
    try { await api.put(`/leave/${id}/status`, { status }); toast.success(`Request ${status}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{isEmployee ? "Time Off" : "Leave Approvals"}</h1>
        <p className="text-sm text-muted-foreground mt-1">{isEmployee ? "Request and track your time off." : "Review and approve employee leave."}</p>
      </div>

      {!isEmployee && <LeaveCalendar items={items} />}
      <div className={`grid gap-4 ${isEmployee ? "lg:grid-cols-1" : "lg:grid-cols-2"}`}>
        <PtoCalculator key={catKey} params={() => (user.role === "bureau_admin" ? { company_id: activeCompanyId } : {})} employees={employees} />
        {!isEmployee && <PtoPolicyCard params={() => (user.role === "bureau_admin" ? { company_id: activeCompanyId } : {})} />}
      </div>
      {!isEmployee && <PtoCategoriesCard params={() => (user.role === "bureau_admin" ? { company_id: activeCompanyId } : {})} onSaved={() => setCatKey((k) => k + 1)} />}

      <div className={`grid gap-4 ${isEmployee ? "lg:grid-cols-3" : ""}`}>
        {isEmployee && (
          <Card className="p-5 lg:col-span-1 h-fit">
            <h3 className="font-semibold font-display mb-4 flex items-center gap-2"><Plus className="w-4 h-4" /> New Request</h3>
            <form data-testid="leave-request-form" className="space-y-3" onSubmit={(e) => { e.preventDefault(); submit(); }}>
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Start</Label><DatePicker testid="leave-start" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} /></div>
                <div className="space-y-1.5"><Label>End</Label><DatePicker testid="leave-end" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} /></div>
              </div>
              <div className="space-y-1.5"><Label>Days</Label><Input data-testid="leave-days" type="number" min="1" value={form.days} onChange={(e) => setForm({ ...form, days: +e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Reason</Label><Textarea data-testid="leave-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2} /></div>
              <Button data-testid="leave-submit-btn" type="submit" className="w-full" disabled={!form.start_date || !form.end_date}>Submit Request</Button>
            </form>
          </Card>
        )}

        <Card className={`p-0 overflow-hidden ${isEmployee ? "lg:col-span-2" : ""}`}>
          <div className="p-5 border-b border-border"><h3 className="font-semibold font-display">Requests</h3></div>
          <Table>
            <TableHeader><TableRow>{!isEmployee && <TableHead>Employee</TableHead>}<TableHead>Type</TableHead><TableHead>Dates</TableHead><TableHead className="text-right">Days</TableHead><TableHead>Status</TableHead>{!isEmployee && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
            <TableBody>
              {items.map((l) => (
                <TableRow key={l.id} data-testid={`leave-row-${l.id}`}>
                  {!isEmployee && <TableCell className="font-medium">{l.employee_name}</TableCell>}
                  <TableCell>{l.type}</TableCell>
                  <TableCell className="font-mono-num text-xs">{fmtDate(l.start_date)} → {fmtDate(l.end_date)}</TableCell>
                  <TableCell className="text-right font-mono-num">{l.days}</TableCell>
                  <TableCell><StatusBadge status={l.status} /></TableCell>
                  {!isEmployee && (
                    <TableCell className="text-right">
                      {l.status === "pending" ? (
                        <div className="flex justify-end gap-1">
                          <Button data-testid={`approve-${l.id}`} size="icon" variant="ghost" onClick={() => setStatus(l.id, "approved")}><Check className="w-4 h-4 text-emerald-600" /></Button>
                          <Button data-testid={`reject-${l.id}`} size="icon" variant="ghost" onClick={() => setStatus(l.id, "rejected")}><X className="w-4 h-4 text-destructive" /></Button>
                        </div>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={isEmployee ? 4 : 6} className="text-center text-muted-foreground py-10">No leave requests.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
